/**
 * What the food editor knows about itself at the moment the user tries to leave the page
 * (df-19223f602623).
 *
 * Read by `mayLeaveWithoutAsking` below. Every field is a plain snapshot of page state, so
 * the decision can be tested without building the page, which needs the whole database stack.
 */
export interface FoodEditorLeaveState {
  /**
   * True when a path that closes the page itself has already settled what happens to the
   * edits, and said so by arming the page's one-shot "do not ask" latch.
   *
   * The page CONSUMES that latch to produce this value, so reading it is a one-way door —
   * which is why it is read first and passed in, rather than being read again in here.
   */
  armedToLeaveWithoutAsking: boolean;
  /**
   * True once the unsaved-changes guard has been built, which happens when the food has
   * finished loading. Before that the form holds only the blank placeholder the constructor
   * seeded, so there is nothing a dialog could ask about.
   */
  guardReady: boolean;
  /** True for a food this user may not edit, which therefore cannot have unsaved edits. */
  isReadOnly: boolean;
  /**
   * True once this page has deleted the food it was editing, and until it starts editing a
   * different one.
   */
  foodWasDeleted: boolean;
}

/**
 * May the food editor close without raising the unsaved-changes dialog, and without handing
 * an answer back to whatever page is waiting underneath?
 *
 * A page whose food is gone has nothing to ask about and nothing to answer with, so it leaves.
 * That covers the long window in which the food is already deleted locally while the server is
 * still being told; the editor stays usable throughout it, and without this the leave both
 * offered to save a food that no longer exists and handed its id back, on which the food list
 * opens the portion screen. Why that matters and what it cost: `df-19223f602623`'s commit.
 *
 * Unlike the latch, this answer is not used up by being read: a leave refused further down
 * still leaves the page in the deleted state, and the next Back must get the same answer.
 */
export function mayLeaveWithoutAsking(state: FoodEditorLeaveState): boolean {
  if (state.armedToLeaveWithoutAsking) return true;
  if (!state.guardReady) return true;
  if (state.isReadOnly) return true;
  return state.foodWasDeleted;
}
